import { Button, Card, Image } from "antd";
import { Theme } from "api/config/chalice-types";
import cx from "classnames";
import { useState } from "react";
import { MdCheck, MdClose } from "react-icons/md";
import Skeleton from "react-loading-skeleton";

type ThemeCardProps = {
  theme: Theme;
  selectedThemes: string[];
  isEditing: boolean;
  handleSelect?: (themeName: string) => void;
  templateCount?: number;
  showRemoveOption?: boolean;
  onRemove?: (themeName: string) => void;
  isLoadingCount?: boolean;
  onClick?: () => void;
};

const ThemeCard = ({
  theme,
  selectedThemes,
  isEditing,
  handleSelect,
  showRemoveOption,
  onRemove,
  templateCount,
  isLoadingCount,
  onClick,
}: ThemeCardProps) => {
  const isSelected = isEditing && selectedThemes.includes(theme.name);
  const [isHovered, setIsHovered] = useState(false);

  const bodyStyle: React.CSSProperties = { padding: 8, textAlign: "left" };

  return (
    <Card
      key={theme.name}
      hoverable={isEditing}
      className={cx("relative h-fit group", {
        "cursor-pointer": isEditing || onClick,
        "border-2 border-antd-colorPrimary": isSelected,
      })}
      styles={{ body: bodyStyle }}
      onClick={
        isEditing && handleSelect ? () => handleSelect(theme.name) : onClick
      }
      onMouseEnter={
        showRemoveOption && onRemove ? () => setIsHovered(true) : undefined
      }
      onMouseLeave={
        showRemoveOption && onRemove ? () => setIsHovered(false) : undefined
      }
      cover={
        <div className="relative w-full aspect-square p-2">
          <Image
            preview={false}
            src={theme.image_url}
            alt={theme.display_name || theme.name}
            placeholder={<Skeleton className="!w-full !h-full" />}
            className="w-full h-full object-cover"
            wrapperClassName="!w-full !h-full rounded-md overflow-hidden"
          />
          {showRemoveOption && onRemove && isHovered && (
            <Button
              type="primary"
              danger
              shape="circle"
              size="small"
              icon={<MdClose className="h-4 w-4" />}
              className="absolute top-3 right-3 z-10 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={(e) => {
                e.stopPropagation(); // Prevent card click
                onRemove(theme.name);
              }}
              title={`Remove ${theme.display_name || theme.name} theme`}
            />
          )}
          {isSelected && (
            <div className="absolute top-3 right-3 z-10">
              <div className="flex items-center justify-center bg-antd-colorPrimary rounded-full p-1 shadow">
                <MdCheck className="text-antd-colorTextLightSolid h-4 w-4" />
              </div>
            </div>
          )}
        </div>
      }
    >
      <p
        className="font-medium text-base truncate m-0"
        title={theme.display_name || theme.name}
      >
        {theme.display_name || theme.name}
      </p>
      <div>
        {isLoadingCount ? (
          <Skeleton width={80} height={12} />
        ) : (
          templateCount && (
            <p className="text-xs text-antd-colorTextSecondary m-0">
              {templateCount} Template{templateCount === 1 ? "" : "s"}
            </p>
          )
        )}
      </div>
    </Card>
  );
};

export default ThemeCard;
